import { CalendarDateTime } from "@sp-crm/core";
import * as React from "react";
import { DatePicker } from "./date-time-picker/date-picker";
import { TimePicker } from "./date-time-picker/time-picker";
import { Toggle } from "./toggle";

interface Props {
    label: string;
    value: CalendarDateTime | null;
    onChange: (newValue: CalendarDateTime | null) => void;
}

export const CalendarDateTimeInput: React.FC<Props> = ({ label, value, onChange }) => {
    const isTimeEnabled = !!value?.hasTime;
    const dateValue = value ? value.dateString : null;
    const timeValue = value?.hasTime ? value.timeString : null;

    const handleDateChange = (iso: string | null) => {
        if (!iso) {
            onChange(null);
            return;
        }
        if (!value) {
            onChange(CalendarDateTime.parse(iso).getOrElse(new CalendarDateTime()));
            return;
        }
        const [year, month, day] = iso.split("-").map(part => parseInt(part, 10));
        onChange(value.forDate(year, month, day));
    };

    const handleToggleChange = (enabled: boolean) => {
        const base = value || new CalendarDateTime();
        onChange(enabled ? base.withTime : base.withoutTime);
    };

    const handleTimeChange = (time: string | null) => {
        const base = value || new CalendarDateTime();
        if (!time) {
            onChange(base.withoutTime);
            return;
        }
        const [hours, minutes] = time.split(":").map(part => parseInt(part, 10));
        onChange(base.forTime(hours, minutes));
    };

    return (
        <div className="w-full">
            {label ? <div className="mb-1">{label}</div> : null}
            <div className="flex items-center space-x-1 lg:space-x-2">
                <div className="flex-1">
                    <DatePicker
                        label={null}
                        value={dateValue}
                        onChange={handleDateChange}
                        disabled={false}
                        required={false}
                        textSize="base"
                        onOpenChange={() => undefined}
                        minValue={null}
                        maxValue={null}
                        visibleYears={20}
                    />
                </div>
                <div className="flex-1">
                    <div className="flex items-center space-x-1 lg:space-x-2">
                        <div>
                            <Toggle
                                label={isTimeEnabled ? null : "Set specific time"}
                                checked={isTimeEnabled}
                                disabled={!value}
                                onChange={handleToggleChange}
                            />
                        </div>
                        {isTimeEnabled ? (
                            <div className="flex-1">
                                <TimePicker
                                    label={null}
                                    value={timeValue}
                                    onChange={handleTimeChange}
                                    disabled={false}
                                    nativeStepMinutes={1}
                                />
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
};
